import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { toast } from 'react-toastify';

const OrganizerFinancialReports = () => {
  const [dashboardData, setDashboardData] = useState({
    total_earnings: {},
    pending_payouts: {},
    paid_amount: {},
    available_balance: {}
  });
  const [earningsByEvent, setEarningsByEvent] = useState([]);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(true);
  const [isLoadingEarnings, setIsLoadingEarnings] = useState(true);
  const [isLoadingRequests, setIsLoadingRequests] = useState(true);
  const [requestAmount, setRequestAmount] = useState('');
  const [requestCurrency, setRequestCurrency] = useState('QAR');
  const [requestNotes, setRequestNotes] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');
    const [earningsPagination, setEarningsPagination] = useState({});
    const [earningsPage, setEarningsPage] = useState(1);
    const [earningsSearch, setEarningsSearch] = useState('');

    const [payoutsPagination, setPayoutsPagination] = useState({});
    const [payoutsPage, setPayoutsPage] = useState(1);
    const [payoutsSearch, setPayoutsSearch] = useState('');
    const [payoutsStatusFilter, setPayoutsStatusFilter] = useState('all');
    const [iban, setIban] = useState('');
    const [bankAccountNumber, setBankAccountNumber] = useState('');
    const [bankAccountCurrency, setBankAccountCurrency] = useState('');

    const [events, setEvents] = useState([]);
    const [selectedEventId, setSelectedEventId] = useState('');
    const [eventAvailableBalance, setEventAvailableBalance] = useState(0);

    const [bankDetails, setBankDetails] = useState([]);
    const [selectedBankDetailId, setSelectedBankDetailId] = useState('');


    // Helper function to get currency symbol
  const getCurrencySymbol = (currency) => {
    switch(currency) {
      case 'QAR': return 'QAR';
      case 'AED': return 'AED';
      case 'USD': return '$';
      case 'EUR': return '€';
      default: return currency;
    }
  };

    const loadBankDetails = async () => {
        try {
            const response = await api.get('/organizer/settings/bank-details');
            if (response.data.status === 'success') {
                setBankDetails(response.data.data || []);
            }
        } catch (error) {
            console.error('Failed to load bank details:', error);
        }
    };


    const loadEvents = async () => {
        try {
            const response = await api.get('/organizer/financial/events'); // create this endpoint in backend
            if (response.data.status === 'success') {
                setEvents(response.data.data || []);
            }
        } catch (error) {
            console.error('Error loading events:', error);
        }
    };

  // Helper function to format amount with currency
  const formatAmount = (amount, currency) => {
    const symbol = getCurrencySymbol(currency);
    const formatted = parseFloat(amount || 0).toFixed(2);
    return currency === 'USD' || currency === 'EUR' ? `${symbol}${formatted}` : `${formatted} ${symbol}`;
  };

  // Get available balance for selected currency
  const getAvailableBalance = (currency) => {
    return parseFloat(dashboardData.available_balance[currency] || 0);
  };

    useEffect(() => {
        if (activeTab === 'earnings') {
            loadEarningsByEvent(1);
        }
    }, [activeTab, earningsSearch]);

    useEffect(() => {
        if (activeTab === 'payouts') {
            loadPayoutRequests(1);
        }
    }, [activeTab, payoutsSearch, payoutsStatusFilter]);


    useEffect(() => {
    loadDashboard();
    loadEarningsByEvent();
    loadPayoutRequests();
        loadBankDetails();
        loadEvents();
  }, []);

  const loadDashboard = async () => {
    try {
      setIsLoadingDashboard(true);
      const response = await api.get('/organizer/financial/dashboard');
      console.log('Dashboard response:', response.data);
      if (response.data.status === 'success') {
        console.log('Setting dashboard data:', response.data.data);
        setDashboardData(response.data.data);
      } else {
        console.warn('Response status not success:', response.data);
      }
    } catch (error) {
      console.error('Error loading dashboard:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setIsLoadingDashboard(false);
    }
  };

    const loadEarningsByEvent = async (pageNumber = 1) => {
        try {
            setIsLoadingEarnings(true);
            const params = {
                page: pageNumber,
                search: earningsSearch || undefined,
            };

            const response = await api.get('/organizer/financial/earnings-by-event', { params });
            console.log('Earnings by event response:', response.data);

            if (response.data.status === 'success') {
                const paginator = response.data.data;
                setEarningsByEvent(paginator.data || []);
                setEarningsPagination(paginator);
                setEarningsPage(pageNumber);
            } else {
                console.warn('Response status not success:', response.data);
            }
        } catch (error) {
            console.error('Error loading earnings:', error);
            console.error('Error details:', error.response?.data);
        } finally {
            setIsLoadingEarnings(false);
        }
    };

    const loadPayoutRequests = async (pageNumber = 1) => {
        try {
            setIsLoadingRequests(true);
            const params = {
                page: pageNumber,
                search: payoutsSearch || undefined,
                status: payoutsStatusFilter !== 'all' ? payoutsStatusFilter : undefined,
            };

            const response = await api.get('/organizer/financial/payout-requests', { params });
            if (response.data.status === 'success') {
                const paginator = response.data.data;
                setPayoutRequests(paginator.data || []);
                setPayoutsPagination(paginator);
                setPayoutsPage(pageNumber);
            }
        } catch (error) {
            console.error('Error loading payout requests:', error);
        } finally {
            setIsLoadingRequests(false);
        }
    };

    const handleCreatePayoutRequest = async (e) => {
        e.preventDefault();

        // Reset form errors
        setError('');
        setSuccess('');

        if (!selectedEventId) {
            toast.error('Please select an event.');
            return;
        }

        try {
            const response = await api.post('/organizer/financial/payout-requests', {
                amount: parseFloat(requestAmount),
                currency: requestCurrency,
                organizer_notes: requestNotes,
                event_id: selectedEventId, // send selected event
                iban,
                bank_account_number: bankAccountNumber,
                bank_account_currency: bankAccountCurrency
            });

            if (response.data.status === 'success') {
                toast.success('Payout request created successfully!');

                // Reset form fields
                setRequestAmount('');
                setRequestNotes('');
                setSelectedEventId('');
                setIban('');
                setBankAccountNumber('');
                setBankAccountCurrency('');
                setEventAvailableBalance(0);

                // Reload dashboard and payout requests
                loadDashboard();
                loadPayoutRequests();
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to create payout request';
            toast.error(message);
        }
    };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'dumped':
        return 'badge-success';
      case 'undumped':
        return 'badge-danger';
      default:
        return 'badge-warning';
    }
  };

    const handleResetEarningsFilters = () => {
        setEarningsSearch('');
        loadEarningsByEvent(1);
    };

    const handleResetPayoutsFilters = () => {
        setPayoutsSearch('');
        setPayoutsStatusFilter('all');
        loadPayoutRequests(1);
    };


    const renderPagination = (pagination, currentPage, fetchFunction, extraClasses = '') => {
        if (!pagination.last_page || pagination.last_page <= 1) return null;

        const current = currentPage;
        const last = pagination.last_page;
        const maxPages = 3;

        let start = Math.max(1, current - Math.floor(maxPages / 2));
        let end = start + maxPages - 1;

        if (end > last) {
            end = last;
            start = Math.max(1, end - maxPages + 1);
        }

        const pageNumbers = [];
        for (let i = start; i <= end; i++) pageNumbers.push(i);

        return (
            <nav className={`mt-4 ${extraClasses}`}>
                <ul className="pagination justify-content-center">
                    <li className={`page-item ${current === 1 ? 'disabled' : ''}`}>
                        <button
                            className="page-link text-gray-900"
                            onClick={() => fetchFunction(current - 1)}
                        >
                            Previous
                        </button>
                    </li>

                    {start > 1 && (
                        <>
                            <li className="page-item">
                                <button
                                    className="page-link text-gray-900"
                                    onClick={() => fetchFunction(1)}
                                >
                                    1
                                </button>
                            </li>
                            {start > 2 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                        </>
                    )}

                    {pageNumbers.map(num => (
                        <li
                            key={num}
                            className={`page-item ${num === current ? 'active' : ''}`}
                        >
                            <button
                                className={`page-link text-gray-900 ${
                                    num === current
                                        ? '!bg-purple-500 !text-white !border-purple-500 border'
                                        : ''
                                }`}
                                onClick={() => fetchFunction(num)}
                            >
                                {num}
                            </button>
                        </li>
                    ))}

                    {end < last && (
                        <>
                            {end < last - 1 && (
                                <li className="page-item disabled">
                                    <span className="page-link">...</span>
                                </li>
                            )}
                            <li className="page-item">
                                <button
                                    className="page-link text-gray-900"
                                    onClick={() => fetchFunction(last)}
                                >
                                    {last}
                                </button>
                            </li>
                        </>
                    )}

                    <li className={`page-item ${current === last ? 'disabled' : ''}`}>
                        <button
                            className="page-link text-gray-900"
                            onClick={() => fetchFunction(current + 1)}
                        >
                            Next
                        </button>
                    </li>
                </ul>
            </nav>
        );
    };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between text-gray-900 font-weight-bold align-items-center mb-4">
        <h5 className="text-gray-900 h5 font-weight-bold">Financial Reports</h5>
      </div>

      {/* Navigation Tabs */}
      <ul className="nav nav-tabs mb-4">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'dashboard' ? 'active' : ''}`}
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'earnings' ? 'active' : ''}`}
            onClick={() => setActiveTab('earnings')}
          >
            Earnings by Event
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'payouts' ? 'active' : ''}`}
            onClick={() => setActiveTab('payouts')}
          >
            Payout Requests
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === 'request' ? 'active' : ''}`}
            onClick={() => setActiveTab('request')}
          >
            Request Payout
          </button>
        </li>
      </ul>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && (
        <div className="row">
          {isLoadingDashboard ? (
            <div className="col-12 text-center">
              <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
              </div>
            </div>
          ) : (
            <>
              <div className="col-md-3 mb-4">
                <div className="card">
                  <div className="card-body">
                    <h5 className="card-title text-muted">Total Earnings</h5>
                    {Object.keys(dashboardData.total_earnings).length > 0 ? (
                      Object.entries(dashboardData.total_earnings).map(([currency, amount]) => (
                        <div key={currency}>
                          <h3 className="mb-0">{formatAmount(amount, currency)}</h3>
                        </div>
                      ))
                    ) : (
                      <h2 className="mb-0">0.00</h2>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-3 mb-4">
                <div className="card">
                  <div className="card-body">
                    <h5 className="card-title text-muted">Pending Payouts</h5>
                    {Object.keys(dashboardData.pending_payouts).length > 0 ? (
                      Object.entries(dashboardData.pending_payouts).map(([currency, amount]) => (
                        <div key={currency}>
                          <h3 className="mb-0 text-warning">{formatAmount(amount, currency)}</h3>
                        </div>
                      ))
                    ) : (
                      <h2 className="mb-0 text-warning">0.00</h2>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-3 mb-4">
                <div className="card">
                  <div className="card-body">
                    <h5 className="card-title text-muted">Paid Amount</h5>
                    {Object.keys(dashboardData.paid_amount).length > 0 ? (
                      Object.entries(dashboardData.paid_amount).map(([currency, amount]) => (
                        <div key={currency}>
                          <h3 className="mb-0 text-success">{formatAmount(amount, currency)}</h3>
                        </div>
                      ))
                    ) : (
                      <h2 className="mb-0 text-success">0.00</h2>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-3 mb-4">
                <div className="card border-primary">
                  <div className="card-body">
                    <h5 className="card-title text-muted">Available Balance <small className="text-muted text-sm">(Can be requested)</small> </h5>
                    {Object.keys(dashboardData.available_balance).length > 0 ? (
                      Object.entries(dashboardData.available_balance).map(([currency, amount]) => (
                        <div key={currency}>
                          <h3 className="mb-0 text-primary">{formatAmount(amount, currency)}</h3>
                        </div>
                      ))
                    ) : (
                      <h2 className="mb-0 text-primary">0.00</h2>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Earnings by Event Tab */}
      {activeTab === 'earnings' && (
          <>
          {/* Earnings Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-4 border border-gray-200">
              <div className="row">
                  <div className="col-md-3 mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                          Search Events
                      </label>
                      <input
                          type="text"
                          value={earningsSearch}
                          onChange={(e) => setEarningsSearch(e.target.value)}
                          placeholder="Search by event name..."
                          className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-purple-500"
                      />
                  </div>
                  <div className="col-md-2 d-flex align-items-end mb-3">
                      <button
                          onClick={handleResetEarningsFilters}
                          className="btn btn-outline-danger w-20"
                          disabled={!earningsSearch}
                      >
                          <i className="fas fa-redo mr-2"></i>
                          Reset
                      </button>
                  </div>
              </div>
          </div>
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Earnings by Event</h5>
            {isLoadingEarnings ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : earningsByEvent.length === 0 ? (
              <p className="text-center text-muted py-5">No earnings data available</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Category</th>
                      <th>Total Revenue</th>
                      <th>Commission Rate</th>
                      <th>Commission</th>
                      <th>Your Earnings</th>
                      <th>Transactions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {earningsByEvent.map((earning) => (
                      <tr key={earning.event_id}>
                        <td>{earning.event_title}</td>
                        <td>{earning.event_category}</td>
                        <td className="font-weight-bold">{formatAmount(earning.total_revenue, earning.currency)}</td>
                        <td>{parseFloat(earning.commission_rate).toFixed(1)}%</td>
                        <td className="text-danger">{formatAmount(earning.total_commission, earning.currency)}</td>
                        <td className="text-success font-weight-bold">{formatAmount(earning.organizer_earnings, earning.currency)}</td>
                        <td>{earning.transaction_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
              {renderPagination(earningsPagination, earningsPage, loadEarningsByEvent)}
          </div>
        </div>
          </>
      )}

      {/* Payout Requests Tab */}
      {activeTab === 'payouts' && (
          <>
          {/* Payout Requests Filters */}
          <div className="bg-white rounded-lg shadow p-4 mb-4 border border-gray-200">
              <div className="row">
                  <div className="col-md-3 mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                          Search
                      </label>
                      <input
                          type="text"
                          value={payoutsSearch}
                          onChange={(e) => setPayoutsSearch(e.target.value)}
                          placeholder="Search by event name"
                          className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-purple-500"
                      />
                  </div>
                  <div className="col-md-2 mb-3">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                          Status
                      </label>
                      <select
                          value={payoutsStatusFilter}
                          onChange={(e) => setPayoutsStatusFilter(e.target.value)}
                          className="input w-full p-2 !border-2 !border-gray rounded-lg outline-none focus:!border-purple-500"
                      >
                          <option value="all">All Status</option>
                          <option value="pending">Pending</option>
                          <option value="dumped">Dumped</option>
                          <option value="undumped">Undumped</option>
                      </select>
                  </div>
                  <div className="col-md-2 d-flex align-items-end mb-3">
                      <button
                          onClick={handleResetPayoutsFilters}
                          className="btn btn-outline-danger w-20"
                          disabled={!payoutsSearch && payoutsStatusFilter === 'all'}
                      >
                          <i className="fas fa-redo mr-2"></i>
                          Reset
                      </button>
                  </div>
              </div>
          </div>
        <div className="card">
          <div className="card-body">
            <h5 className="card-title">Payout Request History</h5>
            {isLoadingRequests ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status">
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : payoutRequests.length === 0 ? (
              <p className="text-center text-muted py-5">No payout requests yet</p>
            ) : (
              <div className="table-responsive">
                <table className="table table-hover">
                  <thead>
                    <tr>
                      <th>Request Date</th>
                      <th>Event Name</th>
                      <th>Amount</th>
                      <th>Status</th>
                      {/*<th>Transaction ID</th>*/}
                      <th>Processed At</th>
                        <th>Bank Transaction ID</th>
                      {/*<th>Approved By</th>*/}
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payoutRequests.map((request) => (
                      <tr key={request.id}>
                        <td>{new Date(request.requested_at).toLocaleDateString()}</td>
                          <td>{request.event_name}</td>
                        <td className="font-weight-bold">{formatAmount(request.requested_amount, request.event?.currency || 'QAR')}</td>
                        <td>
                          <span className={`badge ${getStatusBadgeClass(request.status)}`}>
                            {request.status.toUpperCase()}
                          </span>
                        </td>
                        {/*<td>*/}
                        {/*  {request.transaction_bank_id ? (*/}
                        {/*    <code>{request.transaction_bank_id}</code>*/}
                        {/*  ) : (*/}
                        {/*    <span className="text-muted">-</span>*/}
                        {/*  )}*/}
                        {/*</td>*/}
                        <td>
                          {request.processed_at ? new Date(request.processed_at).toLocaleDateString() : '-'}
                        </td>
                          <td>
                              {request.bank_transaction_id}
                          </td>
                        {/*<td>{request.approved_by_name || '-'}</td>*/}
                        <td>
                          <small className="text-muted">
                            {request.notes && (
                              <div> {request.notes}</div>
                            )}
                            {request.admin_notes && (
                              <div className={request.status === 'rejected' ? 'text-danger' : ''}>
                                <strong>Admin notes:</strong> {request.admin_notes}
                              </div>
                            )}
                          </small>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
              {renderPagination(payoutsPagination, payoutsPage, loadPayoutRequests)}
          </div>
        </div>
          </>
      )}

      {/* Request Payout Tab */}
      {activeTab === 'request' && (
        <div className="row justify-content-center mb-8">
          <div className="col-md-6">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title">Request Payout</h5>

                  <div className="alert !bg-purple-50 mt-8 border-left-yellow mb-4 text-gray-900">
                      <div className="d-flex flex-column">
                          <strong className="mb-2">Available Balances:</strong>
                          {Object.keys(dashboardData.available_balance).length > 0 ? (
                              <div className="d-flex flex-column flex-md-row flex-wrap gap-2 gap-md-0">
                                  {Object.entries(dashboardData.available_balance).map(([currency, amount], index, array) => (
                                      <React.Fragment key={currency}>
                                          <strong className="text-nowrap">
                                              {formatAmount(amount, currency)}
                                          </strong>
                                          {index < array.length - 1 && (
                                              <span className="d-none d-md-inline mx-2">|</span>
                                          )}
                                      </React.Fragment>
                                  ))}
                              </div>
                          ) : (
                              <div className="text-muted">No available balance</div>
                          )}
                      </div>
                  </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                {success && (
                  <div className="alert alert-success" role="alert">
                    {success}
                  </div>
                )}

                  <form onSubmit={handleCreatePayoutRequest}>
                      <div className="form-group">
                          <label htmlFor="requestCurrency">Currency *</label>
                          <select
                              className="form-control"
                              id="requestCurrency"
                              value={requestCurrency}
                              onChange={(e) => setRequestCurrency(e.target.value)}
                              required
                          >
                              {Object.keys(dashboardData.available_balance).map((currency) => (
                                  <option key={currency} value={currency}>
                                      {currency} - Available: {formatAmount(dashboardData.available_balance[currency], currency)}
                                  </option>
                              ))}
                          </select>
                      </div>

                      <div className="form-group">
                          <label htmlFor="selectEvent">Event *</label>
                          <select
                              className="form-control"
                              id="selectEvent"
                              value={selectedEventId}
                              onChange={(e) => {
                                  const eventId = e.target.value;
                                  setSelectedEventId(eventId);

                                  const event = events.find(ev => ev.id === parseInt(eventId));
                                  setEventAvailableBalance(event ? parseFloat(event.available_balance) : 0);
                                  setRequestCurrency(event ? event.currency : 'QAR'); // set currency based on event
                                  setRequestAmount(''); // reset amount
                              }}
                              required
                          >
                              <option value="">Select Event</option>
                              {events.map(event => (
                                  <option key={event.id} value={event.id}>
                                      {event.title} - Available: {formatAmount(event.available_balance, event.currency)}
                                  </option>
                              ))}
                          </select>
                      </div>

                      <div className="form-group">
                          <label htmlFor="requestAmount">Amount *</label>
                          <div className="input-group">
                              <div className="input-group-prepend">
                                  <span className="input-group-text">{getCurrencySymbol(requestCurrency)}</span>
                              </div>
                              <input
                                  type="number"
                                  className="form-control"
                                  id="requestAmount"
                                  value={eventAvailableBalance}
                                  onChange={(e) => setRequestAmount(e.target.value)}
                                  step="0.01"
                                  min="0.01"
                                  max={eventAvailableBalance}
                                  required
                              />
                          </div>
                          <small className="form-text text-muted">
                              Maximum: {formatAmount(eventAvailableBalance, requestCurrency)}
                          </small>
                      </div>

                      <div className="form-group">
                          <label htmlFor="bankDetail">Select Bank Account *</label>
                          <select
                              id="bankDetail"
                              className="form-control"
                              value={selectedBankDetailId}
                              onChange={(e) => {
                                  const bankId = e.target.value;
                                  setSelectedBankDetailId(bankId);

                                  const bank = bankDetails.find(b => b.id === parseInt(bankId));
                                  if (bank) {
                                      setIban(bank.iban);
                                      setBankAccountNumber(bank.bank_account_number);
                                      setBankAccountCurrency(bank.bank_account_currency);
                                  } else {
                                      setIban('');
                                      setBankAccountNumber('');
                                      setBankAccountCurrency('');
                                  }
                              }}
                              required
                          >
                              <option value="">Select Bank Detail</option>
                              {bankDetails.map((bank) => (
                                  <option key={bank.id} value={bank.id}>
                                      {bank.account_name}
                                  </option>
                              ))}
                          </select>
                      </div>


                      {/* New IBAN field */}
                      <div className="form-group">
                          <label htmlFor="iban">IBAN *</label>
                          <input
                              type="text"
                              className="form-control"
                              id="iban"
                              value={iban}
                              onChange={(e) => setIban(e.target.value)}
                              placeholder="Enter your IBAN"
                              required
                              readOnly
                          />
                      </div>

                      {/* New Bank Account Number field */}
                      <div className="form-group">
                          <label htmlFor="bankAccountNumber">Bank Account Number *</label>
                          <input
                              type="text"
                              className="form-control"
                              id="bankAccountNumber"
                              value={bankAccountNumber}
                              onChange={(e) => setBankAccountNumber(e.target.value)}
                              placeholder="Enter your bank account number"
                              required
                              readOnly
                          />
                      </div>

                      {/* New Bank Account Currency field */}
                      <div className="form-group">
                          <label htmlFor="bankAccountCurrency">Bank Account Currency *</label>
                          <input
                              type="text"
                              className="form-control"
                              id="bankAccountCurrency"
                              value={bankAccountCurrency}
                              onChange={(e) => setBankAccountCurrency(e.target.value.toUpperCase())}
                              placeholder="e.g., USD, EUR, QAR"
                              maxLength={3}
                              required
                              readOnly
                          />
                          <small className="form-text text-muted">
                              Enter currency in 3-letter ISO code format (e.g., USD, EUR, QAR)
                          </small>
                      </div>

                      <div className="form-group">
                          <label htmlFor="requestNotes">Notes (Optional)</label>
                          <textarea
                              className="form-control"
                              id="requestNotes"
                              rows="3"
                              value={requestNotes}
                              onChange={(e) => setRequestNotes(e.target.value)}
                              placeholder="Add any additional information for the admin..."
                          ></textarea>
                      </div>

                      <div className="mb-8">
                          <small>
                              <strong>Note:</strong> Your request will be reviewed by the admin. Once approved,
                              you will receive the bank transfer details including the transaction ID as proof of payment.
                          </small>
                      </div>

                      <button
                          type="submit"
                          className="btn p-3 !bg-purple-500 !text-white !font-bold btn-block hover:!bg-purple-600"
                          disabled={Object.keys(dashboardData.available_balance).length === 0 || getAvailableBalance(requestCurrency) <= 0}
                      >
                          Submit Payout Request
                      </button>
                  </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizerFinancialReports;
